import type { ActionCandidate } from '../ai/ActionCandidate';
import type { PassType } from '../ai/ActionCandidate';
import { ElementSystem } from '../ai/ElementSystem';
import { GoalkeeperSystem } from '../ai/GoalkeeperSystem';
import { PassiveSystem, type PassiveActionType, type PassiveModifierResult } from '../ai/PassiveSystem';
import type { EngineTechnique } from '../data/EngineData';
import { DecisionLogger } from '../logs/DecisionLogger';
import type { BallState } from './BallState';
import { GameRules, type OutOfBoundsRestart } from './GameRules';
import type { EngineMatchEvent, EngineMatchEventType } from './MatchEvent';
import type { MatchRng } from './MatchRng';
import type { MatchState } from './MatchState';
import type { PlayerRuntimeState } from './PlayerRuntimeState';
import { PITCH_GEOMETRY } from './PitchGeometry';
import { arrangeKickoff } from './KickoffSetup';
import { OffsideRule } from './OffsideRule';
import { clamp, distance, normalize, scale, subtract, type Vector2 } from './Vector2';

export class ActionResolver {
  constructor(
    private readonly elements: ElementSystem,
    private readonly logger: DecisionLogger,
    private readonly rules = new GameRules(),
    private readonly goalkeepers = new GoalkeeperSystem(),
    private readonly offsides = new OffsideRule(),
    private readonly passives = new PassiveSystem(),
  ) {}

  execute(state: MatchState, candidate: ActionCandidate, rng: MatchRng, options: { prepareTechniques?: boolean } = {}): EngineMatchEvent[] {
    const actor = this.player(state, candidate.actorId);
    if (!actor) return [];
    if (this.requiresPreparation(candidate, options.prepareTechniques === true) && (candidate.type !== 'USE_TECHNIQUE' || state.ball.ownerPlayerId === actor.playerId)) {
      return this.prepareAction(state, actor, candidate);
    }
    return this.executeImmediately(state, actor, candidate, rng);
  }

  private executeImmediately(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate, rng: MatchRng): EngineMatchEvent[] {
    const technique = candidate.type === 'USE_TECHNIQUE'
      ? actor.techniques.find((item) => item.id === candidate.techniqueId)
      : undefined;
    const isPassAction = candidate.type === 'PASS' || candidate.type === 'CLEAR_BALL' || technique?.type === 'PASS';
    if (!isPassAction) state.restartOffsideExemption = undefined;
    switch (candidate.type) {
      case 'PASS': return [this.resolvePass(state, actor, candidate, rng)];
      case 'DRIBBLE': return this.resolveDribble(state, actor, candidate, rng);
      case 'CARRY': return [this.resolveCarry(state, actor, candidate)];
      case 'SHOOT': return [this.resolveShot(state, actor, candidate, rng)];
      case 'TACKLE': return this.resolveTackle(state, actor, candidate, rng);
      case 'CLEAR_BALL': return [this.resolvePass(state, actor, candidate, rng)];
      case 'USE_TECHNIQUE': return this.resolveTechnique(state, actor, candidate, rng);
      default:
        actor.currentAction = candidate.type;
        actor.actionCooldown = 0.5;
        return [];
    }
  }

  update(state: MatchState, rng: MatchRng): void {
    this.resolvePreparedAction(state, rng);
    if (state.ball.state === 'PASSING') {
      const interception = this.resolveInterception(state, rng);
      if (interception) return;
      if ((state.ball.flight?.progress ?? 0) >= 1 || distance(state.ball.position, state.ball.targetPosition) < 0.001) this.resolveReception(state, rng);
    } else if (state.ball.state === 'SHOOTING' && ((state.ball.flight?.progress ?? 0) >= 1 || distance(state.ball.position, state.ball.targetPosition) < 0.001)) {
      this.resolveSave(state, rng);
    } else if (state.ball.state === 'LOOSE') {
      if (this.rules.isBallOut(state.ball.position)) {
        this.resolveOutOfBounds(state);
        return;
      }
      this.resolveLooseBallDuel(state, rng);
    }
  }

  private requiresPreparation(candidate: ActionCandidate, includeTechnique: boolean): boolean {
    return candidate.type === 'PASS' || candidate.type === 'DRIBBLE' || candidate.type === 'SHOOT' || candidate.type === 'CLEAR_BALL'
      || (includeTechnique && candidate.type === 'USE_TECHNIQUE');
  }

  private prepareAction(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate): EngineMatchEvent[] {
    // Only one football can be prepared at a time. The possession check makes
    // a tackle during the wind-up cancel cleanly rather than releasing an old
    // pass after the ball has already changed sides.
    if (state.pendingAction || state.ball.ownerPlayerId !== actor.playerId) return [];
    const delay = candidate.type === 'USE_TECHNIQUE'
      ? state.config.techniquePreparationSeconds
      : candidate.type === 'SHOOT'
      ? state.config.shotPreparationSeconds
      : candidate.type === 'DRIBBLE'
        ? state.config.dribblePreparationSeconds
        : state.config.passPreparationSeconds;
    actor.currentAction = candidate.type;
    actor.actionCooldown = Math.max(actor.actionCooldown, delay);
    state.pendingAction = {
      actorId: actor.playerId,
      candidate: { ...candidate, targetPosition: candidate.targetPosition ? { ...candidate.targetPosition } : undefined },
      executeAt: state.time + delay,
    };
    return [];
  }

  private resolvePreparedAction(state: MatchState, rng: MatchRng): void {
    const pending = state.pendingAction;
    if (!pending || state.time + 1e-9 < pending.executeAt) return;
    state.pendingAction = undefined;
    const actor = this.player(state, pending.actorId);
    if (!actor || state.ball.ownerPlayerId !== actor.playerId) return;
    this.executeImmediately(state, actor, pending.candidate, rng);
  }

  resolvePass(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate, _rng: MatchRng): EngineMatchEvent {
    const receiver = candidate.targetPlayerId ? this.player(state, candidate.targetPlayerId) : undefined;
    const requestedTarget = candidate.targetPosition ?? receiver?.position ?? {
      x: actor.position.x + (actor.teamSide === 0 ? 24 : -24),
      y: actor.position.y,
    };
    const passType = candidate.passType ?? this.inferPassType(actor, requestedTarget);
    const passProfile = this.passProfile(passType);
    const target = requestedTarget;
    const offsideExempt = state.restartOffsideExemption?.teamSide === actor.teamSide;
    state.restartOffsideExemption = undefined;
    const offside = receiver && !offsideExempt ? this.offsides.evaluate(state, actor, receiver) : undefined;
    const technique = candidate.techniqueId ? actor.techniques.find((item) => item.id === candidate.techniqueId) : undefined;
    const directOpponent = state.players
      .filter((player) => player.teamSide !== actor.teamSide)
      .sort((first, second) => distance(first.position, target) - distance(second.position, target)
        || first.playerId.localeCompare(second.playerId))[0];
    const elementMultiplier = directOpponent
      ? this.elements.getElementMultiplier(technique?.element ?? actor.element, directOpponent.element)
      : 1;
    const basePassQuality = actor.stats.pass * 0.45 + actor.stats.control * 0.2
      + (receiver?.stats.control ?? 50) * 0.1 + (receiver?.stats.speed ?? 50) * 0.1
      + (state.teams[actor.teamSide].tactics.build_up ?? 0.5) * 10
      + state.momentum[actor.teamSide] * 0.05 - candidate.riskScore * 20 + passProfile.qualityModifier;
    const passQuality = this.passiveModifier(
      state, actor, candidate.type, basePassQuality, directOpponent, technique, elementMultiplier,
      candidate.type === 'USE_TECHNIQUE', PITCH_GEOMETRY.relativeProgress(actor.teamSide, target.x)
        - PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x) >= 4,
    ).score;
    this.releaseBall(state, actor, 'PASSING', target, state.config.passBallSpeed * passProfile.speedMultiplier, {
      actorId: actor.playerId,
      teamSide: actor.teamSide,
      startedAt: state.time,
      passQuality,
      passType,
      offsideReceiverId: offside?.offside ? receiver?.playerId : undefined,
      offsidePosition: offside?.offside ? { ...receiver!.position } : undefined,
      techniqueId: candidate.techniqueId,
      checkedInterceptors: [],
    }, receiver?.playerId);
    actor.currentAction = candidate.type;
    actor.actionCooldown = state.config.passActionCooldownSeconds;
    const staminaMultiplier = this.passiveModifier(
      state, actor, candidate.type, 1, directOpponent, technique, elementMultiplier,
      candidate.type === 'USE_TECHNIQUE', true,
    ).staminaCostMultiplier;
    actor.staminaCurrent = Math.max(0, actor.staminaCurrent - candidate.staminaCost * 0.12 * staminaMultiplier);
    state.stats.passes[actor.teamSide]++;
    // The balance model observes PASS_STARTED for ordinary, technical and
    // directed-clearance passes alike. Keep possession memory aligned with
    // that observable action so no variant can bypass anti-loop protection.
    this.rememberAction(state, actor, candidate.type === 'USE_TECHNIQUE' || candidate.type === 'CLEAR_BALL'
      ? { ...candidate, type: 'PASS' }
      : candidate);
    const message = candidate.type === 'CLEAR_BALL'
      ? receiver ? `${actor.fullName} despeja con dirección hacia ${receiver.fullName}` : `${actor.fullName} despeja el balón`
      : receiver ? `${actor.fullName} inicia un pase ${this.passLabel(passType)} hacia ${receiver.fullName}` : `${actor.fullName} envía el balón`;
    const event = this.emit(state, 'PASS_STARTED', actor, true, receiver?.playerId, candidate.techniqueId, message);
    event.targetPosition = { ...target };
    event.passType = passType;
    return event;
  }

  resolveReception(state: MatchState, rng: MatchRng): EngineMatchEvent {
    const ball = state.ball;
    const passType = ball.flight?.passType;
    const techniqueId = ball.flight?.techniqueId;
    const receiver = ball.targetPlayerId ? this.player(state, ball.targetPlayerId) : undefined;
    const actor = ball.flight ? this.player(state, ball.flight.actorId) : undefined;
    if (!receiver || !actor) {
      this.makeLoose(state);
      const event = this.emit(state, 'PASS_FAILED', actor ?? this.fallbackActor(state), false, receiver?.playerId, techniqueId, 'El envío queda suelto');
      event.passType = passType;
      return event;
    }
    if (ball.flight?.offsideReceiverId === receiver.playerId) {
      return this.resolveOffside(state, receiver);
    }
    // A named receiver is a running intention, never permission to hand the
    // ball to someone who did not reach it. Without this check, a completed
    // flight could become owned by a distant player and visually teleport on
    // the following movement tick.
    const controlDistance = distance(receiver.position, ball.position);
    if (controlDistance > state.config.controlRadius) {
      this.makeLoose(state, ball.velocity, actor);
      const event = this.emit(state, 'PASS_FAILED', actor, false, receiver.playerId, techniqueId,
        `${receiver.fullName} no llega al envío`);
      event.passType = passType;
      this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
      return event;
    }
    const opponents = state.players.filter((player) => player.teamSide !== receiver.teamSide && distance(player.position, receiver.position) <= 7);
    const pressurePenalty = opponents.length * 8;
    const contextualSpace = Math.max(0, 100 - pressurePenalty * 4);
    const receptionScore = receiver.stats.control * 0.45 + receiver.stats.stamina * 0.15 + receiver.stats.power * 0.1
      + (ball.flight?.passQuality ?? 50) * 0.2 + contextualSpace * 0.1 - pressurePenalty + rng.noise(8);
    if (receptionScore >= state.config.receptionControlThreshold) {
      // The icon and ball meet at the same point before ownership is changed.
      // The distance is bounded above by controlRadius, so this is a local
      // first touch rather than a long-distance correction.
      ball.position = { ...receiver.position };
      this.giveBall(state, receiver);
      receiver.lastReceptionPassType = passType;
      receiver.lastReceptionTime = state.time;
      receiver.lastReceptionFromPlayerId = actor.playerId;
      receiver.actionCooldown = passType === 'CROSS'
        ? state.config.crossReceptionActionCooldownSeconds
        : state.config.receptionActionCooldownSeconds;
      state.momentum[receiver.teamSide] = clamp(state.momentum[receiver.teamSide] + 0.35, -10, 10);
      const event = this.emit(state, 'PASS_RECEIVED', actor, true, receiver.playerId, techniqueId,
        `${actor.fullName} conecta con ${receiver.fullName}`);
      event.passType = passType;
      this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
      return event;
    }
    this.makeLoose(state);
    const event = this.emit(state, 'PASS_FAILED', actor, false, receiver.playerId, techniqueId,
      `${receiver.fullName} no consigue controlar el pase`);
    event.passType = passType;
    this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
    return event;
  }

  resolveDribble(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate, rng: MatchRng): EngineMatchEvent[] {
    const defender = this.dribbleDefender(state, actor);
    actor.currentAction = 'DRIBBLE';
    actor.contextualTarget = candidate.targetPosition ?? actor.targetPosition;
    actor.actionCooldown = state.config.dribbleActionCooldownSeconds;
    this.rememberAction(state, actor, candidate);
    const technique = candidate.techniqueId ? actor.techniques.find((item) => item.id === candidate.techniqueId) : undefined;
    const elementMultiplier = defender
      ? this.elements.getElementMultiplier(technique?.element ?? actor.element, defender.element)
      : 1;
    const actionType = candidate.type === 'USE_TECHNIQUE' ? 'USE_TECHNIQUE' : 'DRIBBLE';
    const actorPassive = this.passiveModifier(state, actor, actionType, 1, defender, technique, elementMultiplier, Boolean(technique), true);
    actor.staminaCurrent = Math.max(0, actor.staminaCurrent - 0.18 * actorPassive.staminaCostMultiplier);
    if (!defender) return [this.resolveCarry(state, actor, candidate)];
    const foulRisk = this.passiveModifier(state, defender, 'TACKLE', 1, actor).foulRiskMultiplier;
    if (rng.chance(clamp(state.config.duelFoulProbability * foulRisk, 0, 1))) {
      return [this.resolveFoul(state, defender, actor)];
    }
    const attackBase = (actor.stats.dribble * 0.4 + actor.stats.control * 0.25 + actor.stats.speed * 0.15
      + actor.stats.power * 0.1 + state.momentum[actor.teamSide] * 0.1 + rng.noise(10))
      * elementMultiplier;
    const attack = this.passiveModifier(
      state, actor, actionType, attackBase, defender, technique, elementMultiplier, Boolean(technique), true,
    ).score;
    const goalkeeperBonus = defender.positionType === 'GK' ? this.goalkeepers.claimBonus(defender, actor.position) : 0;
    const defenseBase = defender.stats.defense * 0.4 + defender.stats.power * 0.2 + defender.stats.speed * 0.15
      + defender.stats.control * 0.1 + goalkeeperBonus + rng.noise(10);
    const defense = this.passiveModifier(
      state, defender, defender.positionType === 'GK' ? 'SAVE' : 'DUEL', defenseBase, actor,
      undefined, this.elements.getElementMultiplier(defender.element, technique?.element ?? actor.element), true,
    ).score;
    if (attack >= defense) {
      const bypassTarget = this.dribbleBypassTarget(actor, defender, candidate.targetPosition ?? actor.targetPosition);
      // Winning a duel starts an actual run beyond the defender immediately;
      // it is not merely a text event. Tactical positioning preserves this
      // contextual target while the player remains in the dribble action.
      actor.contextualTarget = bypassTarget;
      actor.targetPosition = { ...bypassTarget };
      const bypassDuration = technique
        ? state.config.techniqueDribbleBypassSeconds
        : state.config.dribbleBypassSeconds;
      defender.bypassedUntil = Math.max(defender.bypassedUntil ?? -Infinity, state.time + bypassDuration);
      defender.actionCooldown = Math.max(defender.actionCooldown, bypassDuration);
      actor.dribbleAdvantageUntil = Math.max(actor.dribbleAdvantageUntil ?? -Infinity, state.time + (technique ? 3.4 : 1.7));
      const event = this.emit(state, 'DRIBBLE', actor, true, defender.playerId, candidate.techniqueId, `${actor.fullName} supera a ${defender.fullName}`);
      this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
      return [event];
    }
    this.giveBall(state, defender);
    defender.actionCooldown = Math.max(defender.actionCooldown, state.config.recoveryWinnerCooldownSeconds);
    const failed = this.emit(state, 'DRIBBLE_FAILED', actor, false, defender.playerId, candidate.techniqueId, `${defender.fullName} frena el regate`);
    const tackle = this.emit(state, defender.positionType === 'GK' ? 'SAVE' : 'TACKLE', defender, true, actor.playerId, undefined,
      defender.positionType === 'GK' ? `${defender.fullName} sale y se queda con el balón` : `${defender.fullName} recupera la posesión`);
    state.momentum[actor.teamSide] = clamp(state.momentum[actor.teamSide] - 0.8, -10, 10);
    this.logger.attachResult(state.decisionLogs, actor.playerId, failed.type);
    return [failed, tackle];
  }

  private resolveCarry(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate): EngineMatchEvent {
    actor.currentAction = 'CARRY';
    actor.contextualTarget = candidate.targetPosition ?? actor.targetPosition;
    actor.targetPosition = { ...actor.contextualTarget };
    actor.actionCooldown = state.config.dribbleActionCooldownSeconds;
    actor.staminaCurrent = Math.max(0, actor.staminaCurrent - 0.12);
    this.rememberAction(state, actor, candidate);
    const event = this.emit(state, 'CARRY', actor, true, undefined, undefined, `${actor.fullName} conduce el balón`);
    this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
    return event;
  }

  resolveShot(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate, rng: MatchRng): EngineMatchEvent {
    const goalkeeper = state.players.find((player) => player.teamSide !== actor.teamSide && player.positionType === 'GK');
    const technique = candidate.techniqueId ? actor.techniques.find((item) => item.id === candidate.techniqueId) : undefined;
    const goalX = PITCH_GEOMETRY.attackingGoalX(actor.teamSide);
    const target = {
      x: goalX,
      y: clamp(PITCH_GEOMETRY.centre.y + rng.noise(13), PITCH_GEOMETRY.shotTarget.minY, PITCH_GEOMETRY.shotTarget.maxY),
    };
    if (state.pendingPenaltyTakerId === actor.playerId) state.pendingPenaltyTakerId = undefined;
    const distancePenalty = distance(actor.position, target) * 0.35;
    const pressure = state.players.filter((player) => player.teamSide !== actor.teamSide && distance(player.position, actor.position) < 7).length * 6;
    let shotScore = actor.stats.kick * 0.45 + actor.stats.control * 0.15 + actor.stats.power * 0.1
      + (technique?.power ?? 0) * 0.2 + state.momentum[actor.teamSide] * 0.1
      - distancePenalty - pressure + 32 + rng.noise(7);
    const elementMultiplier = goalkeeper
      ? this.elements.getElementMultiplier(technique?.element ?? actor.element, goalkeeper.element)
      : 1;
    shotScore *= elementMultiplier;
    shotScore = this.passiveModifier(
      state, actor, candidate.type === 'USE_TECHNIQUE' ? 'USE_TECHNIQUE' : 'SHOOT', shotScore,
      goalkeeper, technique, elementMultiplier, true, true,
    ).score;
    this.releaseBall(state, actor, 'SHOOTING', target, state.config.shotBallSpeed, {
      actorId: actor.playerId,
      teamSide: actor.teamSide,
      startedAt: state.time,
      shotScore,
      techniqueId: candidate.techniqueId,
      checkedInterceptors: [],
    });
    actor.currentAction = 'SHOOT';
    actor.actionCooldown = state.config.shotActionCooldownSeconds;
    const shotStamina = this.passiveModifier(
      state, actor, candidate.type === 'USE_TECHNIQUE' ? 'USE_TECHNIQUE' : 'SHOOT', 1,
      goalkeeper, technique, elementMultiplier, true, true,
    ).staminaCostMultiplier;
    actor.staminaCurrent = Math.max(0, actor.staminaCurrent - 0.22 * shotStamina);
    state.stats.shots[actor.teamSide]++;
    state.lastShotTime[actor.teamSide] = state.time;
    this.rememberAction(state, actor, candidate);
    return this.emit(state, 'SHOT_STARTED', actor, true, goalkeeper?.playerId, candidate.techniqueId, `${actor.fullName} arma el disparo`);
  }

  resolveTackle(state: MatchState, defender: PlayerRuntimeState, candidate: ActionCandidate, rng: MatchRng): EngineMatchEvent[] {
    const attacker = candidate.targetPlayerId ? this.player(state, candidate.targetPlayerId) : undefined;
    if (!attacker || state.ball.ownerPlayerId !== attacker.playerId || !this.canAttemptTackle(state, defender, attacker)) return [];
    const technique = candidate.techniqueId ? defender.techniques.find((item) => item.id === candidate.techniqueId) : undefined;
    const actionType = candidate.type === 'USE_TECHNIQUE' ? 'USE_TECHNIQUE' : 'TACKLE';
    const foulRisk = this.passiveModifier(state, defender, actionType, 1, attacker, technique).foulRiskMultiplier;
    if (rng.chance(clamp(state.config.duelFoulProbability * foulRisk, 0, 1))) {
      return [this.resolveFoul(state, defender, attacker)];
    }
    const elementMultiplier = this.elements.getElementMultiplier(technique?.element ?? defender.element, attacker.element);
    const tackleBase = (defender.stats.defense * 0.45 + defender.stats.power * 0.2 + defender.stats.speed * 0.15
      + defender.stats.control * 0.1 + (technique?.power ?? 0) * 0.25 + rng.noise(10))
      * elementMultiplier;
    const tackle = this.passiveModifier(
      state, defender, actionType, tackleBase, attacker, technique, elementMultiplier, true,
    ).score;
    const protectionBase = attacker.stats.dribble * 0.3 + attacker.stats.control * 0.3 + attacker.stats.power * 0.2
      + attacker.stats.speed * 0.1 + state.momentum[attacker.teamSide] * 0.1 + rng.noise(10);
    const protection = this.passiveModifier(state, attacker, 'DUEL', protectionBase, defender, undefined, 1, true).score;
    state.lastTackleTime[defender.teamSide] = state.time;
    const tackleStamina = this.passiveModifier(
      state, defender, actionType, 1, attacker, technique, elementMultiplier, true,
    ).staminaCostMultiplier;
    defender.staminaCurrent = Math.max(0, defender.staminaCurrent - 0.16 * tackleStamina);
    if (tackle < protection) {
      defender.actionCooldown = 1.4;
      return [this.emit(state, 'TACKLE', defender, false, attacker.playerId, technique?.id, `${attacker.fullName} protege el balón` )];
    }
    this.giveBall(state, defender);
    defender.actionCooldown = state.config.recoveryWinnerCooldownSeconds;
    attacker.actionCooldown = Math.max(attacker.actionCooldown, state.config.recoveryLoserCooldownSeconds);
    // A clean stop makes the attacker turn and recover his body before he can
    // instantly steal the ball back. A blocking technique is deliberately
    // more decisive, so its visual impact carries into the next few seconds.
    const staggerDuration = technique?.type === 'BLOCK'
      ? state.config.techniqueBlockStaggerSeconds
      : state.config.tackleStaggerSeconds;
    attacker.bypassedUntil = Math.max(attacker.bypassedUntil ?? -Infinity, state.time + staggerDuration);
    attacker.actionCooldown = Math.max(attacker.actionCooldown, staggerDuration);
    state.momentum[defender.teamSide] = clamp(state.momentum[defender.teamSide] + 0.6, -10, 10);
    const event = this.emit(state, 'TACKLE', defender, true, attacker.playerId, technique?.id, `${defender.fullName} recupera la posesión`);
    this.logger.attachResult(state.decisionLogs, defender.playerId, event.type);
    return [event];
  }

  resolveInterception(state: MatchState, rng: MatchRng): EngineMatchEvent | undefined {
    const flight = state.ball.flight;
    if (!flight) return undefined;
    const candidates = state.players
      .filter((player) => player.teamSide !== flight.teamSide && player.positionType !== 'GK'
        && !flight.checkedInterceptors.includes(player.playerId)
        && distance(player.position, state.ball.position) <= state.config.interceptionRadius)
      .sort((a, b) => distance(a.position, state.ball.position) - distance(b.position, state.ball.position) || a.playerId.localeCompare(b.playerId));
    const defender = candidates[0];
    if (!defender) return undefined;
    flight.checkedInterceptors.push(defender.playerId);
    const positioning = Math.max(0, 100 - distance(defender.position, state.ball.position) * 35);
    const aerialPower = flight.passType === 'CROSS' || (flight.passType === 'LOB' && state.ball.height > 1.35)
      ? defender.stats.power * .12 : 0;
    const scoreBase = defender.stats.defense * 0.4 + defender.stats.speed * 0.2 + defender.stats.control * 0.1
      + positioning * 0.2 + aerialPower + state.momentum[defender.teamSide] * 0.1 + rng.noise(9);
    const actor = this.player(state, flight.actorId) ?? defender;
    const passTechnique = flight.techniqueId ? actor.techniques.find((item) => item.id === flight.techniqueId) : undefined;
    const defenderElementMultiplier = this.elements.getElementMultiplier(defender.element, passTechnique?.element ?? actor.element);
    const score = this.passiveModifier(
      state, defender, 'INTERCEPT', scoreBase, actor, undefined, defenderElementMultiplier, true,
    ).score;
    const protectedPassQuality = (flight.passQuality ?? 50) * this.elements.multiplier(passTechnique?.element ?? actor.element, defender.element);
    if (score < protectedPassQuality) return undefined;
    if (score - protectedPassQuality < 10) {
      const techniqueId = flight.techniqueId;
      const incoming = normalize(state.ball.velocity);
      const velocity = {
        x: incoming.x * rng.between(1.4, 2.4) - incoming.y * rng.noise(1.4),
        y: incoming.y * rng.between(1.4, 2.4) + incoming.x * rng.noise(1.4),
      };
      this.makeLoose(state, velocity, defender);
      defender.actionCooldown = Math.max(defender.actionCooldown, .8);
      state.stats.deflections++;
      const event = this.emit(state, 'DEFLECTION', defender, true, actor.playerId, techniqueId,
        `${defender.fullName} desvía el pase y deja el balón suelto`);
      this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
      return event;
    }
    this.giveBall(state, defender);
    defender.actionCooldown = Math.max(defender.actionCooldown, 1.2);
    state.momentum[defender.teamSide] = clamp(state.momentum[defender.teamSide] + 0.8, -10, 10);
    const event = this.emit(state, 'INTERCEPTION', defender, true, actor.playerId, flight.techniqueId, `${defender.fullName} intercepta el pase`);
    this.logger.attachResult(state.decisionLogs, actor.playerId, event.type);
    return event;
  }

  resolveSave(state: MatchState, rng: MatchRng): EngineMatchEvent {
    const flight = state.ball.flight;
    const shooter = flight ? this.player(state, flight.actorId) : undefined;
    if (!flight || !shooter) {
      this.makeLoose(state);
      return this.emit(state, 'OUT', this.fallbackActor(state), true, undefined, undefined, 'El balón sale del campo');
    }
    const goalkeeper = state.players.find((player) => player.teamSide !== shooter.teamSide && player.positionType === 'GK');
    const shotDistance = distance(shooter.position, state.ball.targetPosition);
    const onTargetChance = clamp(0.28 + shooter.stats.kick * 0.004 + shooter.stats.control * 0.002 - shotDistance * 0.004, 0.12, 0.86);
    this.emit(state, 'SHOT', shooter, true, goalkeeper?.playerId, flight.techniqueId, `${shooter.fullName} busca la escuadra`);
    if (!rng.chance(onTargetChance) || !goalkeeper) {
      const restart = this.rules.goalKick(shooter.teamSide);
      const event = this.resolveRestart(state, restart, shooter, flight.techniqueId, 'El disparo se marcha fuera: saque de puerta');
      this.logger.attachResult(state.decisionLogs, shooter.playerId, event.type);
      return event;
    }
    state.stats.shotsOnTarget[shooter.teamSide]++;
    const catchTechniqueAvailable = state.time - state.lastTechniqueTime[goalkeeper.teamSide] >= state.config.minimumTeamTechniqueIntervalSeconds
      && state.time - goalkeeper.lastTechniqueTime >= state.config.minimumPlayerTechniqueIntervalSeconds;
    const catchTechnique = catchTechniqueAvailable
      ? goalkeeper.techniques.find((technique) => technique.type === 'CATCH' && goalkeeper.tpCurrent >= technique.tpCost)
      : undefined;
    const techniquePower = catchTechnique && rng.chance(0.35) ? catchTechnique.power : 0;
    if (catchTechnique && techniquePower > 0) {
      goalkeeper.tpCurrent -= catchTechnique.tpCost;
      goalkeeper.lastTechniqueTime = state.time;
      state.lastTechniqueTime[goalkeeper.teamSide] = state.time;
      state.stats.techniques[goalkeeper.teamSide]++;
      this.emit(state, 'TECHNIQUE', goalkeeper, true, shooter.playerId, catchTechnique.id, `${goalkeeper.fullName} activa ${catchTechnique.name}`);
    }
    const saveElementMultiplier = this.elements.getElementMultiplier(catchTechnique?.element ?? goalkeeper.element, shooter.element);
    const savePassive = this.passiveModifier(
      state, goalkeeper, catchTechnique ? 'USE_TECHNIQUE' : 'SAVE',
      this.goalkeepers.saveScore(goalkeeper, state.ball.targetPosition, techniquePower, rng),
      shooter, catchTechnique, saveElementMultiplier, true,
    );
    const saveScore = savePassive.score;
    goalkeeper.staminaCurrent = Math.max(0, goalkeeper.staminaCurrent - .12 * savePassive.staminaCostMultiplier);
    const outcome = this.goalkeepers.outcome(goalkeeper, flight.shotScore ?? 50, saveScore, rng);
    if (outcome === 'GOAL') {
      state.score[shooter.teamSide]++;
      state.lastGoalConcededAtByTeam[goalkeeper.teamSide] = state.time;
      state.momentum[shooter.teamSide] = clamp(state.momentum[shooter.teamSide] + 2, -10, 10);
      const event = this.emit(state, 'GOAL', shooter, true, goalkeeper.playerId, flight.techniqueId, `¡Gol de ${shooter.fullName}!`);
      this.logger.attachResult(state.decisionLogs, shooter.playerId, event.type);
      this.restartKickoff(state, this.rules.teamAfterGoal(shooter.teamSide));
      return event;
    }
    if (outcome === 'PARRY') {
      this.makeLoose(state, this.goalkeepers.parryVelocity(goalkeeper, rng), goalkeeper);
      goalkeeper.actionCooldown = 1.2;
      state.stats.rebounds++;
      const event = this.emit(state, 'REBOUND', goalkeeper, true, shooter.playerId, catchTechnique?.id ?? flight.techniqueId,
        `${goalkeeper.fullName} rechaza el disparo; queda un rebote`);
      this.logger.attachResult(state.decisionLogs, shooter.playerId, event.type);
      return event;
    }
    this.giveBall(state, goalkeeper);
    goalkeeper.actionCooldown = 2.2;
    // Preserve the incoming special shot when the keeper used no technique of
    // their own. Presentation can then name every supertechnique involved in
    // the save rather than silently replacing it with a generic sentence.
    const event = this.emit(state, 'SAVE', goalkeeper, true, shooter.playerId, catchTechnique?.id ?? flight.techniqueId, `${goalkeeper.fullName} detiene el disparo`);
    this.logger.attachResult(state.decisionLogs, shooter.playerId, event.type);
    return event;
  }

  resolveTechnique(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate, rng: MatchRng): EngineMatchEvent[] {
    const technique = actor.techniques.find((item) => item.id === candidate.techniqueId);
    if (!technique || actor.tpCurrent < Math.max(technique.minTp, technique.tpCost)
      || state.time - state.lastTechniqueTime[actor.teamSide] < state.config.minimumTeamTechniqueIntervalSeconds
      || state.time - actor.lastTechniqueTime < state.config.minimumPlayerTechniqueIntervalSeconds) return [];
    // A dribble supertechnique is a duel tool, never a cosmetic sprint into
    // empty grass. Do this before consuming TP or emitting its animation.
    if (technique.type === 'DRIBBLE' && !this.dribbleDefender(state, actor)) return [];
    actor.tpCurrent -= technique.tpCost;
    actor.lastTechniqueTime = state.time;
    state.lastTechniqueTime[actor.teamSide] = state.time;
    state.stats.techniques[actor.teamSide]++;
    const event = this.emit(state, 'TECHNIQUE', actor, true, candidate.targetPlayerId, technique.id, `${actor.fullName} activa ${technique.name}`);
    const enhanced = { ...candidate, techniqueId: technique.id };
    if (technique.type === 'SHOT') return [event, this.resolveShot(state, actor, enhanced, rng)];
    if (technique.type === 'PASS') return [event, this.resolvePass(state, actor, enhanced, rng)];
    if (technique.type === 'DRIBBLE') return [event, ...this.resolveDribble(state, actor, enhanced, rng)];
    if (technique.type === 'BLOCK') return [event, ...this.resolveTackle(state, actor, enhanced, rng)];
    state.momentum[actor.teamSide] = clamp(state.momentum[actor.teamSide] + technique.power / 30, -10, 10);
    actor.actionCooldown = 2;
    return [event];
  }

  resolveLooseBallDuel(state: MatchState, rng: MatchRng): EngineMatchEvent | undefined {
    const candidates = state.players
      .filter((player) => distance(player.position, state.ball.position) <= state.config.controlRadius)
      .sort((a, b) => distance(a.position, state.ball.position) - distance(b.position, state.ball.position) || a.playerId.localeCompare(b.playerId));
    if (candidates.length === 0) {
      // A deflection can stop between both tactical lines. The closest
      // outfielder is already chasing it, but after a short grace period we
      // resolve the contest when they are close enough instead of allowing a
      // possession-less match to appear frozen.
      const nearest = state.players
        .filter((player) => player.positionType !== 'GK')
        .sort((first, second) => distance(first.position, state.ball.position) - distance(second.position, state.ball.position)
          || first.playerId.localeCompare(second.playerId))[0];
      const looseDuration = state.time - (state.ball.looseSince ?? state.time);
      if (!nearest || looseDuration < 8 || distance(nearest.position, state.ball.position) > 8) return undefined;
      const previousPossession = state.possessionTeam;
      this.giveBall(state, nearest);
      nearest.actionCooldown = 1.5;
      return nearest.teamSide !== previousPossession
        ? this.emit(state, 'TURNOVER', nearest, true, undefined, undefined, `${nearest.fullName} llega al balón dividido`)
        : undefined;
    }
    const contenders = candidates.slice(0, 2).map((player) => ({
      player,
      score: this.looseBallScore(
        player,
        state,
        rng,
        candidates.find((candidate) => candidate.teamSide !== player.teamSide),
      ),
    }));
    contenders.sort((a, b) => b.score - a.score || a.player.playerId.localeCompare(b.player.playerId));
    const winner = contenders[0].player;
    const previousPossession = state.possessionTeam;
    this.giveBall(state, winner);
    winner.actionCooldown = 3;
    return winner.teamSide !== previousPossession
      ? this.emit(state, 'TURNOVER', winner, true, undefined, undefined, `${winner.fullName} gana el balón dividido`)
      : undefined;
  }

  /**
   * Last-resort recovery for a visually stalled phase.  It is deliberately a
   * normal football action (a safe outlet or clearance), rather than moving a
   * player or handing the ball to someone at distance.  This keeps the match
   * deterministic while ensuring an accidental cluster cannot freeze play.
   */
  resolveStalemate(state: MatchState, rng: MatchRng): EngineMatchEvent[] {
    if (state.ball.state === 'LOOSE') {
      const nearest = state.players
        .filter((player) => player.positionType !== 'GK')
        .sort((first, second) => distance(first.position, state.ball.position) - distance(second.position, state.ball.position)
          || first.playerId.localeCompare(second.playerId))[0];
      if (!nearest) return [];
      state.ball.position = { ...nearest.position };
      this.giveBall(state, nearest);
      nearest.actionCooldown = Math.min(nearest.actionCooldown, .5);
      return nearest.teamSide !== state.previousPossessionTeam
        ? [this.emit(state, 'TURNOVER', nearest, true, undefined, undefined, `${nearest.fullName} recupera un balón dividido`)]
        : [];
    }

    const owner = state.ball.ownerPlayerId ? this.player(state, state.ball.ownerPlayerId) : undefined;
    if (!owner) return [];
    const outlet = state.players
      .filter((player) => player.teamSide === owner.teamSide && player.playerId !== owner.playerId)
      .filter((player) => player.positionType !== 'GK' || owner.positionType === 'GK')
      .map((player) => {
        const progress = PITCH_GEOMETRY.relativeProgress(owner.teamSide, player.position.x)
          - PITCH_GEOMETRY.relativeProgress(owner.teamSide, owner.position.x);
        const space = state.players
          .filter((opponent) => opponent.teamSide !== owner.teamSide)
          .reduce((nearest, opponent) => Math.min(nearest, distance(player.position, opponent.position)), 20);
        const travel = distance(owner.position, player.position);
        return { player, score: progress * 1.8 + space * 1.35 + Math.min(travel, 24) * .12 };
      })
      .filter((entry) => distance(entry.player.position, owner.position) >= 7)
      .sort((first, second) => second.score - first.score || first.player.playerId.localeCompare(second.player.playerId))[0]?.player;

    if (!outlet) return [];
    const ownProgress = PITCH_GEOMETRY.relativeProgress(owner.teamSide, owner.position.x);
    const pressured = state.players.some((opponent) => opponent.teamSide !== owner.teamSide
      && distance(opponent.position, owner.position) <= 3.2);
    const candidate: ActionCandidate = {
      type: ownProgress < 38 && pressured ? 'CLEAR_BALL' : 'PASS',
      actorId: owner.playerId,
      targetPlayerId: outlet.playerId,
      targetPosition: { ...outlet.position },
      passType: distance(owner.position, outlet.position) >= 24 ? 'LOB' : 'DRIVEN',
      utilityScore: 0,
      riskScore: 0.15,
      staminaCost: .4,
      tpCost: 0,
      debugReason: 'Salida de seguridad tras una jugada estancada',
    };
    return [this.resolvePass(state, owner, candidate, rng)];
  }

  private looseBallScore(
    player: PlayerRuntimeState,
    state: MatchState,
    rng: MatchRng,
    opponent?: PlayerRuntimeState,
  ): number {
    const baseScore = player.stats.speed * 0.35 + player.stats.control * 0.35 + player.stats.power * 0.3
      + this.goalkeepers.claimBonus(player, state.ball.position) + rng.noise(5);
    return this.passiveModifier(state, player, 'DUEL', baseScore, opponent, undefined, 1, true).score;
  }

  private releaseBall(
    state: MatchState,
    actor: PlayerRuntimeState,
    phase: BallState['state'],
    target: Vector2,
    speed: number,
    flight: Omit<NonNullable<BallState['flight']>, 'origin' | 'duration' | 'progress' | 'arcHeight' | 'curve'>,
    targetPlayerId?: string,
  ): void {
    actor.hasBall = false;
    const origin = { ...actor.position };
    const travel = distance(origin, target);
    const duration = Math.max(phase === 'SHOOTING' ? .5 : .42, travel / Math.max(.1, speed));
    const passProfile = flight.passType ? this.passProfile(flight.passType) : undefined;
    const isLongDistribution = phase === 'PASSING' && travel >= 24;
    const curveSign = target.y >= origin.y ? 1 : -1;
    state.ball = {
      position: origin,
      velocity: scale(normalize(subtract(target, origin)), speed),
      state: phase,
      targetPosition: { ...target },
      targetPlayerId,
      lastTouchPlayerId: actor.playerId,
      lastTouchTeam: actor.teamSide,
      speed,
      height: 0,
      flight: {
        ...flight,
        origin,
        duration,
        progress: 0,
        arcHeight: phase === 'SHOOTING'
          ? clamp(travel * .045, .65, 2.8)
          : clamp(travel * (passProfile?.arcScale ?? (isLongDistribution ? .09 : .035)), passProfile?.minimumArc ?? .25, passProfile?.maximumArc ?? 4.8),
        curve: phase === 'PASSING' && (flight.passType === 'CROSS' || isLongDistribution)
          ? clamp(travel * (flight.passType === 'CROSS' ? .07 : .045), .5, 2.6) * curveSign
          : 0,
      },
    };
  }

  private dribbleBypassTarget(actor: PlayerRuntimeState, defender: PlayerRuntimeState, requestedTarget: Vector2): Vector2 {
    const direction = normalize(subtract(requestedTarget, actor.position));
    const safeDirection = Math.hypot(direction.x, direction.y) > .001
      ? direction
      : { x: actor.teamSide === 0 ? 1 : -1, y: 0 };
    const defenderAhead = Math.max(0, (defender.position.x - actor.position.x) * safeDirection.x
      + (defender.position.y - actor.position.y) * safeDirection.y);
    const requestedDistance = distance(actor.position, requestedTarget);
    const exitDistance = Math.max(requestedDistance, defenderAhead + 3.2);
    return PITCH_GEOMETRY.clampRestart({
      x: actor.position.x + safeDirection.x * exitDistance,
      y: actor.position.y + safeDirection.y * exitDistance,
    });
  }

  private dribbleDefender(state: MatchState, actor: PlayerRuntimeState): PlayerRuntimeState | undefined {
    return state.players
      .filter((player) => player.teamSide !== actor.teamSide && distance(player.position, actor.position) <= 3.2)
      .sort((first, second) => distance(first.position, actor.position) - distance(second.position, actor.position) || first.playerId.localeCompare(second.playerId))[0];
  }

  private canAttemptTackle(state: MatchState, defender: PlayerRuntimeState, attacker: PlayerRuntimeState): boolean {
    const separation = distance(defender.position, attacker.position);
    if (separation > 1.85 || (defender.bypassedUntil ?? -Infinity) > state.time) return false;
    const trailingDistance = (attacker.position.x - defender.position.x) * (attacker.teamSide === 0 ? 1 : -1);
    return trailingDistance <= .45 || separation <= 1.15;
  }


  private giveBall(state: MatchState, player: PlayerRuntimeState, countsAsTurnover = true): void {
    for (const candidate of state.players) candidate.hasBall = candidate.playerId === player.playerId;
    delete player.lastReceptionPassType;
    delete player.lastReceptionTime;
    delete player.lastReceptionFromPlayerId;
    if (state.possessionTeam !== player.teamSide) {
      state.previousPossessionTeam = state.possessionTeam;
      state.lastPossessionChangeTime = state.time;
      if (countsAsTurnover) state.stats.turnovers++;
      if (countsAsTurnover) state.counterWindowByTeam[player.teamSide] = state.time + this.passives.config.counterWindowSeconds;
      state.recentActions[player.teamSide] = [];
    }
    state.possessionTeam = player.teamSide;
    state.ball = {
      position: { ...state.ball.position },
      velocity: { ...player.velocity },
      ownerPlayerId: player.playerId,
      state: 'OWNED',
      targetPosition: { ...player.position },
      lastTouchPlayerId: player.playerId,
      lastTouchTeam: player.teamSide,
      speed: 0,
      height: 0,
    };
    if (state.pendingAction?.actorId !== player.playerId) state.pendingAction = undefined;
  }

  private makeLoose(state: MatchState, velocity: Vector2 = { x: 0, y: 0 }, lastTouch?: PlayerRuntimeState): void {
    const ball = state.ball;
    for (const player of state.players) player.hasBall = false;
    ball.state = 'LOOSE';
    ball.ownerPlayerId = undefined;
    ball.targetPlayerId = undefined;
    ball.targetPosition = { ...ball.position };
    ball.velocity = velocity;
    ball.speed = Math.hypot(velocity.x, velocity.y);
    ball.height = 0;
    ball.looseSince = state.time;
    ball.flight = undefined;
    state.pendingAction = undefined;
    if (lastTouch) {
      ball.lastTouchPlayerId = lastTouch.playerId;
      ball.lastTouchTeam = lastTouch.teamSide;
    }
  }

  private resolveOutOfBounds(state: MatchState): void {
    const actor = state.ball.lastTouchPlayerId ? this.player(state, state.ball.lastTouchPlayerId) : undefined;
    const restart = this.rules.outOfBoundsRestart(state.ball, actor?.teamSide ?? state.possessionTeam);
    const event = this.resolveRestart(state, restart, actor);
    this.logger.attachResult(state.decisionLogs, event.actorId, event.type);
  }

  private resolveRestart(
    state: MatchState,
    restart: OutOfBoundsRestart,
    lastTouch?: PlayerRuntimeState,
    techniqueId?: string,
    message?: string,
  ): EngineMatchEvent {
    const eligible = state.players.filter((candidate) => candidate.teamSide === restart.teamSide
      && (restart.type === 'GOAL_KICK' || candidate.positionType !== 'GK'));
    const taker = restart.type === 'GOAL_KICK'
      ? state.players.find((candidate) => candidate.teamSide === restart.teamSide && candidate.positionType === 'GK') ?? eligible[0]
      : eligible.sort((first, second) => distance(first.position, restart.position) - distance(second.position, restart.position)
        || first.playerId.localeCompare(second.playerId))[0];
    state.ball.position = { ...restart.position };
    taker.position = { ...restart.position };
    taker.velocity = { x: 0, y: 0 };
    taker.contextualTarget = { ...restart.position };
    taker.targetPosition = { ...restart.position };
    this.giveBall(state, taker, false);
    taker.actionCooldown = restart.type === 'GOAL_KICK' ? 4 : 2;
    if (restart.type === 'THROW_IN') state.stats.restarts.throwIns[restart.teamSide]++;
    else if (restart.type === 'CORNER') state.stats.restarts.corners[restart.teamSide]++;
    else state.stats.restarts.goalKicks[restart.teamSide]++;
    const defaultMessage = restart.type === 'THROW_IN'
      ? `Saque de banda para ${state.teams[restart.teamSide].name}`
      : restart.type === 'CORNER'
        ? `Córner para ${state.teams[restart.teamSide].name}`
        : `Saque de puerta para ${state.teams[restart.teamSide].name}`;
    const event = this.emit(state, restart.type, taker, true, lastTouch?.playerId, techniqueId, message ?? defaultMessage);
    event.restartType = restart.type;
    if (restart.type === 'THROW_IN' || restart.type === 'CORNER' || restart.type === 'GOAL_KICK') {
      state.restartOffsideExemption = { teamSide: restart.teamSide, type: restart.type };
    }
    return event;
  }

  private resolveOffside(state: MatchState, receiver: PlayerRuntimeState): EngineMatchEvent {
    const position = PITCH_GEOMETRY.clampRestart(state.ball.flight?.offsidePosition ?? receiver.position);
    const restartSide = receiver.teamSide === 0 ? 1 : 0;
    const taker = state.players
      .filter((player) => player.teamSide === restartSide && player.positionType !== 'GK')
      .sort((first, second) => distance(first.position, position) - distance(second.position, position)
        || first.playerId.localeCompare(second.playerId))[0]
      ?? state.players.find((player) => player.teamSide === restartSide)!;
    taker.position = { ...position };
    taker.velocity = { x: 0, y: 0 };
    state.ball.position = { ...position };
    this.giveBall(state, taker);
    taker.actionCooldown = 2;
    state.stats.offsides[receiver.teamSide]++;
    state.stats.restarts.freeKicks[restartSide]++;
    const event = this.emit(state, 'OFFSIDE', receiver, false, taker.playerId, undefined,
      `Fuera de juego de ${receiver.fullName}`);
    event.restartType = 'FREE_KICK';
    return event;
  }

  private resolveFoul(
    state: MatchState,
    offender: PlayerRuntimeState,
    victim: PlayerRuntimeState,
  ): EngineMatchEvent {
    const penalty = PITCH_GEOMETRY.isInOwnPenaltyArea(offender.teamSide, victim.position);
    const position = penalty
      ? { x: PITCH_GEOMETRY.worldX(offender.teamSide, 12), y: PITCH_GEOMETRY.centre.y }
      : PITCH_GEOMETRY.clampRestart(victim.position);
    victim.position = { ...position };
    victim.velocity = { x: 0, y: 0 };
    state.ball.position = { ...position };
    this.giveBall(state, victim, false);
    offender.actionCooldown = Math.max(offender.actionCooldown, 1.4);
    victim.actionCooldown = penalty ? 0.8 : 2;
    state.stats.foulsCommitted[offender.teamSide]++;
    if (penalty) {
      state.stats.restarts.penalties[victim.teamSide]++;
      state.pendingPenaltyTakerId = victim.playerId;
    } else {
      state.stats.restarts.freeKicks[victim.teamSide]++;
    }
    const event = this.emit(state, 'FOUL', offender, false, victim.playerId, undefined,
      penalty ? `Penalti: falta de ${offender.fullName} sobre ${victim.fullName}`
        : `Falta de ${offender.fullName} sobre ${victim.fullName}`);
    event.restartType = penalty ? 'PENALTY' : 'FREE_KICK';
    return event;
  }

  private restartKickoff(state: MatchState, teamSide: 0 | 1): void {
    const player = arrangeKickoff(state.players, teamSide);
    this.giveBall(state, player, false);
    player.actionCooldown = 1.2;
    state.kickoffTacticalRefresh = true;
  }

  private inferPassType(actor: PlayerRuntimeState, target: Vector2): PassType {
    const travel = distance(actor.position, target);
    if (actor.positionType === 'GK') return 'LOB';
    return travel >= 22 ? 'DRIVEN' : 'GROUND';
  }

  private passProfile(passType: PassType): {
    speedMultiplier: number;
    arcScale: number;
    minimumArc: number;
    maximumArc: number;
    qualityModifier: number;
  } {
    switch (passType) {
      case 'DRIVEN': return { speedMultiplier: 1.2, arcScale: .018, minimumArc: .18, maximumArc: .75, qualityModifier: 2 };
      case 'LOB': return { speedMultiplier: .8, arcScale: .12, minimumArc: 1.8, maximumArc: 5.4, qualityModifier: -2 };
      case 'CROSS': return { speedMultiplier: .92, arcScale: .09, minimumArc: 1.4, maximumArc: 4.6, qualityModifier: -4 };
      case 'THROUGH': return { speedMultiplier: 1.12, arcScale: .02, minimumArc: .16, maximumArc: .7, qualityModifier: 1 };
      default: return { speedMultiplier: 1, arcScale: .028, minimumArc: .18, maximumArc: 1.05, qualityModifier: 0 };
    }
  }

  private passLabel(passType: PassType): string {
    switch (passType) {
      case 'DRIVEN': return 'tenso';
      case 'LOB': return 'bombeado';
      case 'CROSS': return 'en forma de centro';
      case 'THROUGH': return 'al hueco';
      default: return 'raso';
    }
  }

  private emit(
    state: MatchState,
    type: EngineMatchEventType,
    actor: PlayerRuntimeState,
    success: boolean,
    targetId?: string,
    techniqueId?: string,
    message: string = type,
  ): EngineMatchEvent {
    const event: EngineMatchEvent = {
      sequence: state.events.length,
      second: Number(state.time.toFixed(3)),
      type,
      team: actor.teamSide,
      actorId: actor.playerId,
      actorSourcePlayerId: actor.sourcePlayerId ?? actor.playerId,
      actorAlias: actor.alias,
      targetId,
      targetSourcePlayerId: targetId ? this.player(state, targetId)?.sourcePlayerId ?? targetId : undefined,
      targetAlias: targetId ? this.player(state, targetId)?.alias : undefined,
      position: { ...state.ball.position },
      success,
      message,
      homeScore: state.score[0],
      awayScore: state.score[1],
      action: actor.currentAction,
      techniqueId,
    };
    state.events.push(event);
    state.recentEvents.push(event);
    if (state.recentEvents.length > 32) state.recentEvents.splice(0, state.recentEvents.length - 32);
    return event;
  }

  private passiveModifier(
    state: MatchState,
    player: PlayerRuntimeState,
    actionType: PassiveActionType,
    baseScore: number,
    opponent?: PlayerRuntimeState,
    technique?: EngineTechnique,
    elementMultiplier = 1,
    isDecisive = false,
    isVerticalAction = false,
  ): PassiveModifierResult {
    const counterStartedAt = state.counterWindowByTeam[player.teamSide] - this.passives.config.counterWindowSeconds;
    return this.passives.applyPassiveModifier({
      player,
      opponent,
      actionType,
      baseScore,
      technique,
      elementMultiplier,
      context: {
        minute: state.time / 60,
        scoreDiff: state.score[player.teamSide] - state.score[player.teamSide === 0 ? 1 : 0],
        secondsSincePossessionGain: state.possessionTeam === player.teamSide
          && state.time <= state.counterWindowByTeam[player.teamSide]
          ? state.time - counterStartedAt
          : Infinity,
        secondsSinceConceded: state.time - state.lastGoalConcededAtByTeam[player.teamSide],
        isDecisive,
        isVerticalAction,
      },
    });
  }

  private player(state: MatchState, id: string): PlayerRuntimeState | undefined {
    return state.players.find((player) => player.playerId === id);
  }

  private fallbackActor(state: MatchState): PlayerRuntimeState {
    return state.players[0];
  }

  private rememberAction(state: MatchState, actor: PlayerRuntimeState, candidate: ActionCandidate): void {
    const memory = state.recentActions[actor.teamSide];
    const direction = actor.teamSide === 0 ? 1 : -1;
    const targetX = candidate.targetPosition?.x ?? actor.position.x + direction;
    memory.push({
      actorId: actor.playerId,
      type: candidate.type,
      targetId: candidate.targetPlayerId,
      second: state.time,
      startProgress: PITCH_GEOMETRY.relativeProgress(actor.teamSide, actor.position.x),
      targetProgress: PITCH_GEOMETRY.relativeProgress(actor.teamSide, targetX),
    });
    if (memory.length > state.config.recentActionMemorySize) {
      memory.splice(0, memory.length - state.config.recentActionMemorySize);
    }
  }
}
